import React from 'react';

import {
  IPharmacyDetailModel,
  PermissionsOfAdmin,
  ServiceTypes,
  convertToDecimals,
  currency,
  fallbackText,
  representNullValue,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';

import strings from '../../../../../localization';
import PharmacyOperation from '../../../../SelfService/PharmacyOperation';
import { getDayMonthDateYear } from '../../../../Reports/helpers';
import { toProfessionalPharmacy } from '../../../AdminConfiguration/SalesforceTable/helper';

const isSelfService = ServiceTypes.self === ServiceHandler.getService();
const hourlyRateKey = 'hourlyRate';
const campaignKey = 'campaignKey';
const crossPlatformKey = 'crossPlatformKey';

const planKey = 'planKey';

interface IPharmacyProfileConfig {
  details: IPharmacyDetailModel;
  language: string;
  software: string;
  successAction: string;
  selectedPreferences: string;
  can:(permission: PermissionsOfAdmin) => boolean;
  isAutoSyncRecord?: boolean;
}

export const handleSuffix = (
  value?: string | number | null,
  suffix?: string,
) => {
  if (!value) {
    return representNullValue;
  }

  const formattedSuffix = suffix ? ` ${suffix}` : '';

  return `${value}${formattedSuffix}`;
};

export const handleValue = (value?: string | number | null) => {
  if (!value) {
    return representNullValue;
  }

  return value;
};

export const handleBooleanValue = (value: boolean) => {
  if (!value) {
    return strings.no;
  }

  return strings.yes;
};

export const formatLeadSource = (broadcastGroupName?: string | null) =>
  (broadcastGroupName
    ? `${strings.broadcastGroup} (${broadcastGroupName})`
    : strings.broadcastGroup);

export const convertDecimal = (rate: number | null): string | null => {
  if (rate) {
    return `${currency}${convertToDecimals(rate)}`;
  }
  return `${currency}0`;
};

export const pharmacyProfileConfig = ({
  details,
  language,
  software,
  successAction,
  selectedPreferences,
  isAutoSyncRecord,
  can,
}: IPharmacyProfileConfig) => {
  const { pharmacy, planName } = details ?? {};
  const {
    pharmacyShortName,
    phone,
    email,
    emergencyPhone,
    administrator,
    code,
    useLSE,
    number,
    fax,
    province,
    address,
    postalCode,
    region,
    city,
    primaryContactEmail,
    primaryContactMobile,
    primaryContactName,
    primaryContactPhone,
    primaryContactPhoneExt,
    secondaryContactEmail,
    name,
    secondaryContactMobile,
    secondaryContactName,
    secondaryContactPhone,
    secondaryContactPhoneExt,
    accountingContactEmail,
    accountingContactMobile,
    accountingContactName,
    accountingContactPhone,
    accountingContactPhoneExt,
    hourlyRate,
    hourlyRateTech,
    banner,
    pharmacyHourlyRate,
    communicationEmail,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    studentWeekDayHourlyRate,
    studentWeekendHourlyRate,
    technicianWeekDayHourlyRate,
    technicianWeekendHourlyRate,
    pharmacistWeekDayHourlyRate,
    pharmacistWeekendHourlyRate,
    studentHourlyRate = 0,
    monthlyPostingReminder,
    entitlement,
    syncTechnicianShifts,
    syncPharmacistShifts,
    portedFromPG = false,
    pharmacyWeekDayHourlyRateForStudent,
    pharmacyWeekendHourlyRateForStudent,
    pharmacyWeekDayHourlyRateForTech,
    pharmacyWeekendHourlyRateForTech,
    pharmacyHourlyRateForStudent,
    pharmacyHourlyRateForTech,
  } = pharmacy ?? {};

  const {
    leadEmail,
    leadName,
    campaignName,
    broadcastGroupId,
    broadcastGroupName,
    promoHourlyRate,
    totalCredits,
    creditsUsed,
    validUntil,
  } = entitlement ?? {};

  const canEditCrossPlatformSettings = can(PermissionsOfAdmin.CrossPlatformSettings);

  const validUntilFormat = validUntil
    ? getDayMonthDateYear(validUntil)
    : fallbackText;

  const hourlyRates = [
    {
      key: 1,
      label: strings.formatString(
        strings.singleDayFormat,
        `${strings.pharmacist}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(hourlyRate, pharmacyHourlyRate),
    },
    {
      key: 2,
      label: strings.formatString(
        strings.multiWeekdayFormat,
        `${strings.pharmacist}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(pharmacistWeekDayHourlyRate, pharmacyWeekDayHourlyRate),
    },
    {
      key: 3,
      label: strings.formatString(
        strings.multiWeekendFormat,
        `${strings.pharmacist}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(pharmacistWeekendHourlyRate, pharmacyWeekendHourlyRate),
    },
    {
      key: 4,
      label: strings.formatString(
        strings.singleDayFormat,
        `${strings.technician}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(hourlyRateTech, pharmacyHourlyRateForTech),
    },
    {
      key: 5,
      label: strings.formatString(
        strings.multiWeekdayFormat,
        `${strings.technician}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(technicianWeekDayHourlyRate, pharmacyWeekDayHourlyRateForTech),
    },
    {
      key: 6,
      label: strings.formatString(
        strings.multiWeekendFormat,
        `${strings.technician}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(technicianWeekendHourlyRate, pharmacyWeekendHourlyRateForTech),
    },
    isSelfService && {
      key: 10,
      label: strings.formatString(
        strings.singleDayFormat,
        `${strings.student}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(studentHourlyRate, pharmacyHourlyRateForStudent),
    },
    isSelfService && {
      key: 11,
      label: strings.formatString(
        strings.multiWeekdayFormat,
        `${strings.student}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(studentWeekDayHourlyRate, pharmacyWeekDayHourlyRateForStudent),
    },
    isSelfService && {
      key: 12,
      label: strings.formatString(
        strings.multiWeekendFormat,
        `${strings.student}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(studentWeekendHourlyRate, pharmacyWeekendHourlyRateForStudent),
    },
  ].filter(Boolean) as Array<{ key: number; label: string; value: any }>;

  let generalConfig = [
    {
      key: 0,
      label: strings.email,
      value: handleValue(email),
    },
    {
      key: 1,
      label: strings.ownerCompanyName,
      value: handleValue(name),
    },
    {
      key: 2,
      label: strings.shortPharmacyName,
      value: handleValue(pharmacyShortName),
      maxLength: 20,
    },
    {
      key: 3,
      label: strings.banner,
      value: handleValue(banner),
    },
    {
      key: 4,
      label: strings.bannerNumber,
      value: handleValue(number),
    },
    {
      key: 5,
      label: strings.software,
      value: handleValue(software),
    },
    {
      key: 6,
      label: strings.contactPreferences,
      value: handleValue(selectedPreferences),
    },
    {
      key: 7,
      label: strings.preferredLanguage,
      value: handleValue(language),
    },
    {
      key: 8,
      label: strings.pharmaplanID,
      value: handleValue(code),
    },
    {
      key: 9,
      label: strings.administrator,
      value: handleValue(administrator),
    },
    {
      key: planKey,
      label: strings.planType,
      value: planName ?? fallbackText,
    },
  ];

  if (!isSelfService || isAutoSyncRecord) {
    generalConfig = generalConfig.filter((item) =>
      item.key !== planKey);
  }

  let arr = [
    {
      key: 0,
      title: strings.general,
      keepExpanded: true,
      cardContent: generalConfig,
    },
    {
      key: 1,
      title: strings.coordinates,
      cardContent: [
        {
          key: 0,
          label: strings.pharmacyPhone,
          value: handleValue(phone),
        },
        {
          key: 1,
          label: strings.pharmacyFax,
          value: handleValue(fax),
        },
        {
          key: 2,
          label: strings.emergencyPhone,
          value: handleValue(emergencyPhone),
        },
        {
          key: 3,
          label: strings.address,
          value: handleValue(address),
        },
        {
          key: 4,
          label: strings.city,
          value: handleValue(city),
        },
        {
          key: 5,
          label: strings.province,
          value: handleValue(province),
        },
        {
          key: 6,
          label: strings.postalCode,
          value: handleValue(postalCode),
        },
        {
          key: 7,
          label: strings.region,
          value: handleValue(region),
        },
      ],
    },
    {
      key: 2,
      title: strings.operationalInfo,
      customComponent: (
        <PharmacyOperation
          successAction={successAction}
          title=""
          customStyle={{ padding: 0 }}
          event={pharmacy}
        />
      ),
    },
    {
      key: 3,
      title: strings.contactDetails,
      cardContent: [
        {
          key: 0,
          label: strings.primaryContactName,
          value: handleValue(primaryContactName),
        },
        {
          key: 1,
          label: strings.primaryContactPhone,
          value: handleValue(primaryContactPhone),
        },
        {
          key: 2,
          label: strings.contactPhoneExtension,
          value: handleValue(primaryContactPhoneExt),
        },
        {
          key: 3,
          label: strings.primaryContactMobile,
          value: handleValue(primaryContactMobile),
        },
        {
          key: 4,
          label: strings.primaryContactEmail,
          value: handleValue(primaryContactEmail),
        },
        {
          key: 5,
          label: strings.andACopyTo,
          value: handleValue(communicationEmail),
        },
        {
          key: 6,
          label: strings.secondaryContactName,
          value: handleValue(secondaryContactName),
        },
        {
          key: 7,
          label: strings.secondaryContactPhone,
          value: handleValue(secondaryContactPhone),
        },
        {
          key: 8,
          label: strings.contactPhoneExtension,
          value: handleValue(secondaryContactPhoneExt),
        },
        {
          key: 9,
          label: strings.secondaryContactMobile,
          value: handleValue(secondaryContactMobile),
        },
        {
          key: 10,
          label: strings.secondaryContactEmail,
          value: handleValue(secondaryContactEmail),
        },
        {
          key: 11,
          label: strings.accountingContactName,
          value: handleValue(accountingContactName),
        },
        {
          key: 12,
          label: strings.accountingContactPhone,
          value: handleValue(accountingContactPhone),
        },
        {
          key: 13,
          label: strings.contactPhoneExtension,
          value: handleValue(accountingContactPhoneExt),
        },
        {
          key: 14,
          label: strings.accountingContactMobile,
          value: handleValue(accountingContactMobile),
        },
        {
          key: 15,
          label: strings.accountingContactEmail,
          value: handleValue(accountingContactEmail),
        },
      ],
    },
    {
      key: 4,
      title: strings.preferences,
      cardContent: [
        {
          key: 0,
          label: strings.useLabSchedulingExt,
          value: handleBooleanValue(useLSE),
        },
        {
          key: 1,
          label: strings.monthlyPostingReminder,
          value: handleBooleanValue(monthlyPostingReminder),
        },
      ].filter(Boolean) as Array<{ key: number; label: string; value: any }>,
    },
    {
      key: hourlyRateKey,
      title: strings.hourlyRates,
      cardContent: hourlyRates,
    },
    {
      key: campaignKey,
      title: strings.campaignDetails,
      cardContent: [
        {
          key: 1,
          label: strings.leadName,
          value: handleValue(leadName),
        },
        {
          key: 2,
          label: strings.leadEmail,
          value: handleValue(leadEmail),
        },
        {
          key: 3,
          label: strings.campaignSource,
          value: handleValue(campaignName),
        },
        broadcastGroupId && {
          key: 4,
          label: strings.leadSource,
          value: formatLeadSource(broadcastGroupName),
        },
        {
          key: 5,
          label: strings.promoHourlyRate,
          value: convertDecimal(promoHourlyRate ?? 0),
        },
        {
          key: 6,
          label: strings.totalCredits,
          value: handleValue(totalCredits),
        },
        {
          key: 7,
          label: strings.creditsUsed,
          value: handleValue(creditsUsed),
        },
        {
          key: 8,
          label: strings.creditsValidUntil,
          value: validUntilFormat,
        },
      ].filter(Boolean) as Array<{ key: number; label: string; value: any }>,
    },
    {
      key: crossPlatformKey,
      title: strings.crossPlatformSettings,
      cardContent: [
        {
          key: 0,
          label: strings.postTechnicianShiftsOnPG,
          value: handleBooleanValue(
            portedFromPG
              ? false
              : syncTechnicianShifts ?? true,
          ),
        },
        {
          key: 1,
          label: strings.postPharmacistShiftsOnPG,
          value: handleBooleanValue(
            portedFromPG
              ? false
              : syncPharmacistShifts ?? true,
          ),
        },
      ],
    },
  ];

  if (isSelfService || !entitlement) {
    arr = arr.filter((item) =>
      campaignKey !== item.key);
  }

  if (isSelfService || !canEditCrossPlatformSettings) {
    arr = arr.filter((item) =>
      item.key !== crossPlatformKey);
  }

  return arr;
};
